import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import FiltersCard from '../../../components/FiltersCard';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import ServerPagination from '../../../components/ServerPagination';
import { Alert, Button, DataTable, EmptyValue, FilterField, FilterGrid, Form, ListPage, PageHeader, RowActions, StatusBadge } from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  materialCatalogService,
  type CatalogBin,
  type CatalogMaterial,
  type CatalogOptions,
  type CatalogPage,
  type StockedFilter,
} from '../../../services/materialCatalogService';
import {
  activeCatalogueFilterCount,
  catalogueFiltersFromParams,
  catalogueMaterialLabel,
  catalogueParams,
  countLabel,
  EMPTY_CATALOGUE_FILTERS,
  sitesHoldingPath,
  STOCKED_OPTIONS,
  type CatalogueFilters,
} from '../catalogue';
import BinStatusBadge from '../components/BinStatusBadge';
import MaterialFormModal from '../components/MaterialFormModal';
import MaterialsSubnav from '../components/MaterialsSubnav';
import OwnerPill from '../components/OwnerPill';
import { formatQuantity } from '../utils/adjustInventory';
import { locationKindLabel, siteTitle } from '../utils/siteDrillThrough';

export const CATALOG_MANAGE_PERMISSION = 'materials:catalog_manage';
const PAGE_SIZE_DEFAULT = 50;

type Load = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: CatalogPage };

const quantity = (value: number | null, unit: string | null) => (value === null ? <EmptyValue /> : formatQuantity(value, unit));

/** Where one material is: every bin holding it, by site and location, with its level. */
const WhereStocked: React.FC<{ material: CatalogMaterial }> = ({ material }) => {
  const columns: DataTableColumn<CatalogBin>[] = [
    {
      key: 'site',
      header: 'Site',
      mobile: 'title',
      cell: (bin) => (
        <Link to={`/locations?site=${encodeURIComponent(bin.site.id)}`} className="tw-font-medium">
          {siteTitle(bin.site)}
        </Link>
      ),
    },
    {
      key: 'location',
      header: 'Storage Location',
      mobile: 'subtitle',
      cell: (bin) => (
        <span>
          {bin.location.name}
          <span className="tw-text-content-muted"> · {locationKindLabel(bin.location.type)}</span>
        </span>
      ),
    },
    { key: 'bin', header: 'Bin', mobileLabel: 'Bin', className: 'tw-whitespace-nowrap', cell: (bin) => bin.code },
    { key: 'owner', header: 'Owner', mobileLabel: 'Owner', cell: (bin) => <OwnerPill owner={bin.owner} fallback="–" /> },
    {
      key: 'onHand',
      header: 'On Hand',
      align: 'right',
      mobile: 'trailing',
      className: 'tw-whitespace-nowrap tw-tabular-nums',
      cell: (bin) => (
        <span className={bin.onHand < 0 ? 'tw-font-semibold tw-text-danger' : ''}>{formatQuantity(bin.onHand, material.unitOfMeasure)}</span>
      ),
    },
    {
      key: 'band',
      header: 'Min / Max',
      align: 'right',
      mobileLabel: 'Min / Max',
      className: 'tw-whitespace-nowrap tw-tabular-nums',
      cell: (bin) =>
        bin.minQuantity === null && bin.maxQuantity === null ? (
          <EmptyValue />
        ) : (
          `${bin.minQuantity ?? '–'} / ${bin.maxQuantity ?? '–'}`
        ),
    },
    { key: 'status', header: 'Status', mobileLabel: 'Status', cell: (bin) => <BinStatusBadge status={bin.status} /> },
  ];

  return (
    <div className="tw-bg-surface-alt tw-px-4 tw-py-3">
      <p className="tw-mb-2 tw-text-sm tw-text-content-secondary">
        {catalogueMaterialLabel(material)} is in {countLabel(material.binCount, 'bin', 'bins')} at{' '}
        {countLabel(material.locationCount, 'storage location', 'storage locations')} on{' '}
        {countLabel(material.siteCount, 'site', 'sites')}.
      </p>
      <DataTable framed columns={columns} data={material.bins} rowKey={(bin) => bin.id} size="sm" />
    </div>
  );
};

/** The material master list (MT-C01): every material, where it is stocked, and adding one. */
const CataloguePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = useMemo(() => catalogueFiltersFromParams(searchParams), [searchParams]);
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [pending, setPending] = useState<CatalogueFilters>(applied);
  const [searchInput, setSearchInput] = useState(applied.search);
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [options, setOptions] = useState<CatalogOptions | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<CatalogMaterial | null | 'new'>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(CATALOG_MANAGE_PERMISSION);

  // The URL is the source of truth; a link or Back resets what the panel shows.
  useEffect(() => {
    setPending(applied);
    setSearchInput(applied.search);
  }, [applied]);

  useEffect(() => {
    materialCatalogService
      .options()
      .then(setOptions)
      .catch(() => setOptions({ units: [], owners: [], types: [] }));
  }, []);

  const loadPage = useCallback(async () => {
    setLoad({ status: 'loading' });
    try {
      setLoad({ status: 'ready', data: await materialCatalogService.list({ ...applied, page, pageSize }) });
    } catch (error) {
      setLoad({ status: 'error', message: error instanceof Error ? error.message : 'Could not load the material catalogue' });
    }
  }, [applied, page, pageSize]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const apply = (filters: CatalogueFilters, nextPage = 1, note: string | null = null) => {
    setSavedNote(note);
    setSearchParams(catalogueParams(filters, nextPage));
  };
  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const data = load.status === 'ready' ? load.data : null;
  const count = activeCatalogueFilterCount(applied);

  const columns: DataTableColumn<CatalogMaterial>[] = [
    {
      // ACT-04: the expand chevron leads the row, in its own fixed column on every row.
      key: 'expand',
      header: '',
      width: '2.75rem',
      mobile: 'hidden',
      cell: (material) =>
        material.binCount > 0 ? (
          <button
            type="button"
            className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded tw-text-content-secondary hover:tw-bg-surface-hover hover:tw-text-content"
            aria-expanded={expanded.has(material.id)}
            aria-label={`${expanded.has(material.id) ? 'Hide' : 'Show'} where ${catalogueMaterialLabel(material)} is stocked`}
            onClick={(event) => {
              event.stopPropagation();
              toggle(material.id);
            }}
          >
            <i
              className={`bi bi-chevron-right tw-transition-transform ${expanded.has(material.id) ? 'tw-rotate-90' : ''}`}
              aria-hidden="true"
            />
          </button>
        ) : null,
    },
    {
      key: 'code',
      header: 'Code',
      mobile: 'title',
      className: 'tw-whitespace-nowrap',
      cell: (material) => <span className="tw-font-semibold tw-text-content">{material.code ?? <EmptyValue />}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      mobile: 'subtitle',
      className: 'tw-min-w-[16rem]',
      cell: (material) =>
        material.description ? (
          <span className="tw-line-clamp-2" title={material.description}>
            {material.description}
          </span>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: 'type',
      header: 'Type',
      mobileLabel: 'Type',
      className: 'tw-min-w-[10rem]',
      cell: (material) => material.type?.name ?? <EmptyValue />,
    },
    {
      key: 'unit',
      header: 'Units of Measure',
      mobileLabel: 'Units of Measure',
      className: 'tw-whitespace-nowrap',
      // Stored values are shown as stored (LANG-03); new and corrected materials are lowercase.
      cell: (material) => material.unitOfMeasure ?? <EmptyValue />,
    },
    { key: 'owner', header: 'Owner', mobileLabel: 'Owner', cell: (material) => <OwnerPill owner={material.owner} fallback="–" /> },
    {
      key: 'where',
      header: 'Where Stocked',
      mobileLabel: 'Where Stocked',
      className: 'tw-whitespace-nowrap',
      cell: (material) =>
        material.binCount === 0 ? (
          <span className="tw-text-content-muted">Not stocked</span>
        ) : (
          <span>
            {/* TBL-11: a count is a link to the related list, filtered to this material. */}
            <Link to={sitesHoldingPath(material.id)} onClick={(event) => event.stopPropagation()}>
              {countLabel(material.siteCount, 'site', 'sites')}
            </Link>
            <span className="tw-text-content-muted"> · {countLabel(material.binCount, 'bin', 'bins')}</span>
          </span>
        ),
    },
    {
      key: 'onHand',
      header: 'On Hand',
      align: 'right',
      mobile: 'trailing',
      className: 'tw-whitespace-nowrap tw-tabular-nums',
      cell: (material) => (
        <span className={material.onHand !== null && material.onHand < 0 ? 'tw-font-semibold tw-text-danger' : ''}>
          {quantity(material.onHand, material.unitOfMeasure)}
        </span>
      ),
    },
    {
      key: 'flags',
      header: 'Flags',
      mobileLabel: 'Flags',
      cell: (material) =>
        material.winterMaterial || material.vehicleRequiredForWithdrawal ? (
          <span className="tw-flex tw-flex-wrap tw-gap-1">
            {material.winterMaterial ? <StatusBadge status="winter" variant="info" label="Winter" /> : null}
            {material.vehicleRequiredForWithdrawal ? <StatusBadge status="vehicle" variant="secondary" label="Vehicle Required" /> : null}
          </span>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: 'actions',
      header: '',
      pin: 'right',
      align: 'right',
      mobile: 'trailing',
      width: '3.5rem',
      hidden: !canManage,
      cell: (material) => (
        <div onClick={(event) => event.stopPropagation()}>
          <RowActions maxInline={0} actions={[{ kind: 'edit', label: `Edit Material: ${material.code ?? material.description ?? ''}`, onClick: () => setEditing(material) }]} />
        </div>
      ),
    },
  ];

  return (
    <ListPage>
      <PageHeader
        title="Locations"
        subtitle="The material master list: every material, and the sites, storage locations and bins that hold it."
        actions={[
          { key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', overflow: true, onClick: () => void loadPage() },
          {
            key: 'add',
            label: 'Add Material',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canManage,
            onClick: () => setEditing('new'),
          },
        ]}
      />

      <MaterialsSubnav />

      <FiltersCard
        title="Filters"
        collapsible
        showClearButton
        onClear={() => apply(EMPTY_CATALOGUE_FILTERS)}
        hasActiveFilters={count > 0}
        activeFilterCount={count}
        showApplyButton
        onApply={() => apply({ ...pending, search: searchInput })}
        applyDisabled={
          catalogueParams({ ...pending, search: searchInput }).toString() === catalogueParams(applied).toString()
        }
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: (value) => apply({ ...applied, search: value }),
          placeholder: 'Code, description or type',
          label: 'Search the material catalogue',
        }}
      >
        <FilterGrid>
          <FilterField span={2}>
            <MultiSelectDropdown
              label="Type"
              options={options?.types ?? []}
              value={pending.typeIds}
              onChange={(values) => setPending((current) => ({ ...current, typeIds: values.map(String) }))}
              getValue={(type) => type.id}
              formatOption={(type) => type.name}
              placeholder="All Types"
            />
          </FilterField>
          <FilterField>
            <Form.Label htmlFor="catalogue-stocked">Stocked</Form.Label>
            <Form.Select
              id="catalogue-stocked"
              value={pending.stocked}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                setPending((current) => ({ ...current, stocked: event.target.value as StockedFilter }))
              }
            >
              {STOCKED_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </FilterField>
        </FilterGrid>
      </FiltersCard>

      {savedNote ? (
        <p className="tw-mb-2 tw-text-sm tw-text-success" role="status">
          {savedNote}
        </p>
      ) : data ? (
        <p className="tw-mb-2 tw-text-sm tw-text-content-muted" aria-live="polite">
          {countLabel(data.total, 'material', 'materials')}
        </p>
      ) : null}

      <ListPage.Body>
        {load.status === 'error' ? (
          <Alert variant="danger">
            <p className="tw-mb-2">{load.message}</p>
            <Button size="sm" variant="outline-danger" onClick={() => void loadPage()}>
              Try Again
            </Button>
          </Alert>
        ) : (
          <DataTable
            fill
            columns={columns}
            data={data?.materials ?? []}
            rowKey={(material) => material.id}
            loading={load.status === 'loading'}
            onRowClick={(material) => (material.binCount > 0 ? toggle(material.id) : undefined)}
            isExpanded={(material) => expanded.has(material.id)}
            renderExpanded={(material) => <WhereStocked material={material} />}
            emptyMessage={count || applied.search ? 'No materials match these filters.' : 'The catalogue is empty.'}
          />
        )}
      </ListPage.Body>

      {data && data.total > 0 ? (
        <ServerPagination
          currentPage={data.page}
          totalPages={Math.max(1, Math.ceil(data.total / data.pageSize))}
          totalCount={data.total}
          pageSize={data.pageSize}
          onPageChange={(next) => apply(applied, next)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            apply(applied, 1);
          }}
          loading={load.status === 'loading'}
        />
      ) : null}

      <MaterialFormModal
        show={editing !== null}
        material={editing === 'new' ? null : editing}
        options={options}
        onHide={() => setEditing(null)}
        onSaved={(saved) => {
          const added = editing === 'new';
          setEditing(null);
          // Show the material just saved (search for its code), and say what happened (STATE-06).
          apply({ ...EMPTY_CATALOGUE_FILTERS, search: saved.code ?? '' }, 1, `${added ? 'Added' : 'Saved'} ${catalogueMaterialLabel(saved)}`);
        }}
      />
    </ListPage>
  );
};

export default CataloguePage;
